/**
 * @fileoverview Sidebar to attach files in conversations, with validation, internationalization and optimized rendering.
 * Allows attaching images, PDFs and other files, showing errors and controlling the sending.
 * @module AttachFilesSidebar
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.0.0 - Dynamic support, advanced validation and translations.
 */

/**
 * Component Organism to attach files in conversation sidebar.
 *
 * Implements validation with Yup, integration with react-hook-form, internationalization and optimized rendering.
 *
 * @component AttachFilesSidebar
 * @category Component - Organism
 * @param {AttachFilesSidebarProps} props - Component properties.
 * @returns {JSX.Element} Sidebar rendered to attach files.
 *
 * @example
 * <AttachFilesSidebar maxFiles={3} maxFileSize={5242880} accept=".png,.jpg,.pdf" handleSubmit={onSend} />
 *
 * @see DropzoneFileUploader Component to load and preview files
 * @see RightModalHeader Header of the sidebar
 */
import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import FormProviderMC from '../components/FormProviderMC';
import DropzoneFileUploader from '../components/DropzoneFileUploader';
import RightModalHeader from '../layouts/Modal/RightModalHeader';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import ButtonComponent from '../components/ButtonMC';

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const AttachFilesSidebar = memo(({
  maxFiles = 5,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  accept,
  title,
  handleSubmit,
  initialAttachments
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleClickOnClose = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'AttachFilesSidebar' }
    });
  }, [dispatch]);

  // Memoize schema to avoid recreation
  const validationSchema = React.useMemo(() => (
    Yup.object().shape({
      attachment: Yup.array()
        .of(
          Yup.mixed()
            .test('is-file', t('file_uploader.invalidFileType', 'Archivo inválido'), (value) => value instanceof File)
            .test('max-size', t('file_uploader.maxFileSize', { maxFileSize: Math.round(maxFileSize / (1024 * 1024)) }), (value) => value && value.size <= maxFileSize)
        )
        .min(1, t('file_uploader.minFiles'))
        .max(maxFiles, t('file_uploader.maxFiles', { maxFiles }))
        .required(t('file_uploader.minFiles'))
    })
  ), [maxFiles, maxFileSize, t]);

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      attachment: []
    },
    mode: 'onChange'
  });

  useEffect(() => {
    if (!initialAttachments?.length) return;
    const files = Array.isArray(initialAttachments) ? initialAttachments : [initialAttachments];
    formMethods.reset({ attachment: [...files] });
    formMethods.trigger('attachment');
  }, [initialAttachments, formMethods]);

  // Memoize selected files to avoid unnecessary renders
  const selectedFiles = React.useMemo(() => {
    const files = formMethods.watch('attachment');
    return Array.isArray(files) ? files : [];
  }, [formMethods.watch('attachment')]);

  // Dynamic send handler by props
  const handleSend = useCallback(() => {
    formMethods.handleSubmit(({ attachment }) => {
      if (handleSubmit) handleSubmit(attachment);
      handleClickOnClose();
    })();
  }, [formMethods, handleSubmit, handleClickOnClose]);

  return (
    <div className="tw-wrapper tw-flex tw-flex-col">
      <RightModalHeader
        title={title || t('file_uploader.title')}
        onClose={handleClickOnClose}
      />
      <div className="tw-flex-1 tw-overflow-auto tw-p-4">
        <FormProviderMC methods={formMethods}>
          <DropzoneFileUploader
            maxFiles={maxFiles}
            maxSize={maxFileSize}
            displayMode="card"
            name="attachment"
            accept={accept || '*'}
            placeholder={t('file_uploader.placeholder')}
            className="tw-mb-3"
            fileContainerClassName="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-px-3"
            defaultValue={selectedFiles}
          />
          {selectedFiles.length > 0 && (
            <>
              <hr className="tw-my-4 tw-border-gray-200" />
              <div className="tw-flex tw-justify-end">
                <ButtonComponent
                  text={t('file_uploader.send')}
                  onClick={handleSend}
                  size="medium"
                />
              </div>
            </>
          )}
        </FormProviderMC>
      </div>
    </div>
  );
});

AttachFilesSidebar.displayName = 'AttachFilesSidebar';

export default AttachFilesSidebar;
